import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import {
  OnboardingContextType,
  OnboardingData,
  OAuthContact,
} from "../types/onboarding";
import {
  loadOnboardingData,
  saveOnboardingData,
  clearOnboardingData,
} from "../utils/storage";
import supabase from "../lib/supabaseClient";

const defaultData: OnboardingData = {
  firstName: "",
  lastName: "",
  companyName: "",
  companyWebsite: "",
  selectedContacts: [],
  metrics: [],
  engineeringMetrics: [],
  integrations: [],
  step: 1,
  completed_steps: [],
  onboarding_complete: false,
};

// Helper to throw if context methods are used outside provider
const noop = (methodName: string) => () => {
  throw new Error(
    `OnboardingContext.${methodName} must be used within an OnboardingProvider`
  );
};

export const OnboardingContext = createContext<OnboardingContextType>({
  data: defaultData,
  isInitialized: false,
  setAccountInfo: noop("setAccountInfo"),
  setRole: noop("setRole"),
  setSelectedContacts: noop("setSelectedContacts"),
  setMetrics: noop("setMetrics"),
  setEngineeringMetrics: noop("setEngineeringMetrics"),
  setIntegrations: noop("setIntegrations"),
  reset: noop("reset"),
});

interface ProviderProps {
  children: ReactNode;
}

export const OnboardingProvider: React.FC<ProviderProps> = ({ children }) => {
  const [data, setData] = useState<OnboardingData>(defaultData);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize onboarding data on mount
  useEffect(() => {
    const initializeData = async () => {
      console.log("OnboardingContext: Starting initialization");

      try {
        // Load from localStorage for fast initial render
        // The useOnboardingFlow hook will handle server fetching and syncing
        console.log("OnboardingContext: Checking localStorage");
        const localData = loadOnboardingData();
        console.log("OnboardingContext: Found local data:", !!localData);

        if (localData) {
          console.log("OnboardingContext: Using localStorage data");
          setData((prev) => ({
            ...defaultData,
            ...localData,
            // Ensure arrays are initialized
            selectedContacts: localData.selectedContacts || [],
            metrics: localData.metrics || [],
            engineeringMetrics: localData.engineeringMetrics || [],
            integrations: localData.integrations || [],
            completed_steps: localData.completed_steps || [],
          }));
        }

        // Mark as initialized immediately
        console.log("OnboardingContext: Setting initialized to true");
        setIsInitialized(true);
      } catch (error) {
        console.error("OnboardingContext: Initialization error:", error);
        console.log(
          "OnboardingContext: Setting initialized to true (error fallback)"
        );
        setIsInitialized(true);
      }
    };

    initializeData();
  }, []);

  // Note: Auto-save to localStorage is now handled by useOnboardingFlow hook
  // to ensure server and localStorage stay synchronized

  const setAccountInfo = useCallback(
    (info: {
      firstName: string;
      lastName: string;
      companyName: string;
      companyWebsite: string;
    }) => {
      setData((prev) => ({
        ...prev,
        ...info,
        // Update step if this is the first time setting account info
        step: (prev.step || 1) < 2 ? 2 : prev.step || 1,
      }));
    },
    []
  );

  const setRole = useCallback((role: string) => {
    setData((prev) => ({
      ...prev,
      role,
      // Update step if this is the first time setting role
      step: (prev.step || 1) < 3 ? 3 : prev.step || 1,
    }));
  }, []);

  const setSelectedContacts = useCallback(
    (selectedContacts: OAuthContact[]) => {
      setData((prev) => ({
        ...prev,
        selectedContacts,
        // Update step if this is the first time setting contacts
        step: (prev.step || 1) < 5 ? 5 : prev.step || 1,
      }));
    },
    []
  );

  const setMetrics = useCallback((metrics: string[]) => {
    setData((prev) => ({
      ...prev,
      metrics,
      // Update step if this is the first time setting metrics
      step: (prev.step || 1) < 6 ? 6 : prev.step || 1,
    }));
  }, []);

  const setEngineeringMetrics = useCallback((engineeringMetrics: string[]) => {
    setData((prev) => ({
      ...prev,
      engineeringMetrics,
      // Update step if this is the first time setting engineering metrics
      step: (prev.step || 1) < 7 ? 7 : prev.step || 1,
    }));
  }, []);

  const setIntegrations = useCallback((integrations: string[]) => {
    setData((prev) => ({
      ...prev,
      integrations,
      // Update step if this is the first time setting integrations
      step: (prev.step || 1) < 8 ? 8 : prev.step || 1,
    }));
  }, []);

  const reset = useCallback(() => {
    clearOnboardingData();
    setData(defaultData);
  }, []);

  // Enhanced context value with data validation helpers
  const contextValue: OnboardingContextType = {
    data,
    isInitialized,
    setAccountInfo,
    setRole,
    setSelectedContacts,
    setMetrics,
    setEngineeringMetrics,
    setIntegrations,
    reset,
  };

  return (
    <OnboardingContext.Provider value={contextValue}>
      {children}
    </OnboardingContext.Provider>
  );
};
